import firebase from "firebase";
import {DigitalSignagesTypes} from "@/enums/DigitalSignagesTypes";
import {serializer} from "@/api/shared/serializer";
import moment from "moment";
import {ValidationRules} from "@/utils/validation-rules";
import {DigitalSignageDateOptions} from "@/enums/DigitalSignageDateOptions";
import {DigitalSignageEndDateOptions} from "@/enums/DigitalSignageEndDateOptions";
import TimeRangeDropdown from "@/components/pickers/TimeRangeDropdown.vue";
import {Week} from "@/consts/Week";
import {DigitalSignagePageBuilder} from "@/models/digital-signage/DigitalSignagePage";

export default {
  name: "ContentCreatorPublishDlg",
  data() {
    return {
      dialog: false,
      content: null,
      search: '',
      isSelectedAll: false,
      isIndeterminate: false,
      selectedDigitalSignage: [],
      resolve: null,
      reject: null,
      showError: false,
      isDisable: false,
      isExpirationDate: false,
      expirationDate: null,
      startDate: null,
      minDate: moment(new Date()).format("YYYY-MM-DD"),
      currentStep: 1,
      titlePage: "",
      timeInSeconds: 10,
      displayOn: "",
      displayForDays: [],
      time: {
        start: moment("09:00", "HH:mm").format(this.$store.state.facilityModule.selectedFacility.timeFormat),
        end: moment("17:00", "HH:mm").format(this.$store.state.facilityModule.selectedFacility.timeFormat)
      },
      panel: [0],
      alert: true,
      isPublishWarning: false,
      publishWarning: '',
      update: null,
    }
  },
  components: {
    TimeRangeDropdown
  },
  computed: {
    digitalSignageList() {
      return this.$store.state.digitalSignageModule.currentDigitalSignageList;
    },
    digitalSignageListSort() {
      return this.search
        ? this.digitalSignageList.filter(signage => signage.channelName.toLowerCase().indexOf(this.search.toLowerCase()) !== -1)
        : this.digitalSignageList;
    },
    rules() {
      return {
        timeInSeconds: [
          ValidationRules.positiveNumber
        ]
      }
    },
    DigitalSignageDateOptions() {
      return DigitalSignageDateOptions;
    },
    digitalSignageDateOptions() {
      return Object.values(this.DigitalSignageDateOptions);
    },
    digitalSignageEndDateOptions() {
      return DigitalSignageEndDateOptions
    },
    weekDays() {
      return Week;
    },
    isFirstStep() {
      return this.currentStep === 1;
    },
    isSecondStep() {
      return this.currentStep === 2;
    }
  },
  mounted() {
    this.$store.dispatch("getDigitalSignages");
    this.displayOn = this.DigitalSignageDateOptions.AllTheTime;
    this.startDate = this.minDate;
  },
  methods: {
    open(content) {
      this.dialog = true;
      this.alert = true;
      this.content = content;
      this.startDate = this.minDate;
      this.$store.dispatch("getDigitalSignages");

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    close() {
      this.dialog = false;
      this.content = null;
      this.search = '';
      this.isSelectedAll = false;
      this.isIndeterminate = false;
      this.selectedDigitalSignage = [];
      this.showError = false;
      this.isExpirationDate = false;
      this.expirationDate = null;
      this.startDate = null;
      this.currentStep = 1;
      this.resolve(false);
    },
    async publishToDigitalSignage() {
      this.isDisable = true;
      let isPublished = false;

      if(this.selectedDigitalSignage.length && this.timeInSeconds > 0) {
        this.showError = false;
        for(let i = 0; i < this.selectedDigitalSignage.length; i++) {
          const selectedDigitalSignageId = this.selectedDigitalSignage[i];
          const selectedDigitalSignage = this.digitalSignageList.filter(x => x.id === selectedDigitalSignageId)[0];

          if(selectedDigitalSignage.pages.find(page => page.contentCreatorId === this.content.id)) {
            await this.openPublishWarning(selectedDigitalSignage.channelName)
              .then(async (data) => {
                if(data.add) {
                  await firebase
                    .firestore()
                    .collection("digitalSignage")
                    .doc(selectedDigitalSignageId)
                    .get()
                    .then((querySnapshot: any) => {
                      const data: any = serializer.documentSnapshotToDocument(querySnapshot);
                      const page = this.generateDigitalSignagePage(data.pages.length + 1);
                      isPublished = true;

                      querySnapshot.ref.update({
                        pages: firebase.firestore.FieldValue.arrayUnion({...page})
                      })
                    })
                }

                if(data.update) {
                  await firebase
                    .firestore()
                    .collection("digitalSignage")
                    .doc(selectedDigitalSignageId)
                    .get()
                    .then((querySnapshot: any) => {
                      const data: any = serializer.documentSnapshotToDocument(querySnapshot);
                      const pages = data.pages.map(x => {
                        if(x?.contentCreatorId === this.content.id && x?.type === DigitalSignagesTypes.ContentCreator) {
                          return {...this.generateDigitalSignagePage(x.order)};
                        }

                        return x;
                      });
                      isPublished = true;

                      querySnapshot.ref.update({
                        pages: pages
                      });
                    });
                }

                if(data.cancel) {
                  this.isPublishWarning = false;
                  return;
                }
              })
          } else {
            await firebase
              .firestore()
              .collection("digitalSignage")
              .doc(selectedDigitalSignageId)
              .get()
              .then((querySnapshot: any) => {
                const data: any = serializer.documentSnapshotToDocument(querySnapshot);
                const page = this.generateDigitalSignagePage(data.pages.length + 1);
                isPublished = true;

                querySnapshot.ref.update({
                  pages: firebase.firestore.FieldValue.arrayUnion({...page})
                })
              })
          }
        }

        this.resolve({ agree: isPublished });
        this.isDisable = false;
        this.isPublishWarning = false;
        this.close();
      } else {
        this.showError = true;
        this.isDisable = false;
        this.isPublishWarning = false;
      }
    },
    generateDigitalSignagePage(order) {
      const page = new DigitalSignagePageBuilder()
        .setContentCreatorId(this.content.id)
        .setEnabled(this.startDate === this.minDate)
        .setName(this.content.name)
        .setOrder(order)
        .setTimeInSeconds(this.timeInSeconds)
        .setTitle(this.titlePage)
        .setType(DigitalSignagesTypes.ContentCreator)
        .setUrl("")
        .setHasExpirationDate(this.isExpirationDate)
        .setDisplayDate(this.displayOn ? true : false)
        .setDisplayOn(this.displayOn)
        .setDisplayForDays(this.displayForDays)
        .setStartDate(this.startDate)
        .setTime(this.time)
        .build();

      if(this.isExpirationDate) {
        page.expirationDate = this.expirationDate;
      }

      return page;
    },
    openPublishWarning(channelName) {
      this.isPublishWarning = true;
      this.publishWarning = this.$t("ContentPublishedToDlg.PageAlreadyPublished").replace("[digitalSignageName]", channelName);

      return new Promise((update, cancel) => {
        this.update = update;
        this.cancel = cancel;
      });
    },
    selectAll() {
      if(this.isSelectedAll) {
        this.selectedDigitalSignage = this.digitalSignageList.map(x => x.id);
      } else {
        this.selectedDigitalSignage = [];
      }
    },
    onTimeRangeChange(timeRange) {
      this.time.start = timeRange.startTime
      this.time.end = timeRange.endTime
    },
    startDayIn(startDate) {
      if(!startDate) {
        return null;
      }

      const count = moment(startDate).diff(this.minDate, 'days');

      this.alert = (count === 0);

      return count;
    },
    addPage() {
      this.update({ update: false, add: true, cancel: false });
    },
    updatePage() {
      this.update({ update: true, add: false, cancel: false });
    },
    cancelPage() {
      this.update({ update: false, add: false, cancel: true });
    }
  },
  watch: {
    selectedDigitalSignage(value) {
      if(value.length) {
        if(value.length === this.digitalSignageList.length) {
          this.isSelectedAll = true;
        } else {
          this.isSelectedAll = false;
          this.isIndeterminate = true;
          this.isSelectedAll = true;
        }
      } else {
        this.isSelectedAll = false;
        this.isIndeterminate = false;
      }
    }
  }
}